import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { CheckCircle2, Circle } from "lucide-react";

const DemoTimeline = ({ steps }) => {
  const defaultStep = useMemo(() => {
    return steps.find((s) => s.status === "active") || steps[0];
  }, [steps]);

  const [selectedStep, setSelectedStep] = useState(defaultStep);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-success" />;
      case "active":
        return <Circle className="h-5 w-5 fill-primary text-primary" />;
      default:
        return <Circle className="h-5 w-5 text-muted" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "border-success";
      case "active":
        return "border-primary";
      default:
        return "border-muted";
    }
  };

  return (
      <div className="mx-auto max-w-4xl">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Timeline */}
          <div className="relative">
            {/* Single straight rail (no per-item segments) */}
            <div className="pointer-events-none absolute left-[18px] top-6 bottom-6 w-px bg-border" />

            <div className="space-y-4">
              {steps.map((step, index) => {
                const isSelected = selectedStep?.id === step.id;

                return (
                    <motion.div
                        key={step.id}
                        className="relative pl-12"
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.08 }}
                    >
                      {/* Icon anchored consistently so it always aligns with the rail */}
                      <div className="absolute left-0 top-6 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-background">
                        {getStatusIcon(step.status)}
                      </div>

                      <Card
                          className={`cursor-pointer border-2 transition-all duration-300 ${
                              isSelected
                                  ? `${getStatusColor(step.status)} shadow-lg`
                                  : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => setSelectedStep(step)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <CardTitle className="text-base">
                                  {step.title}
                                </CardTitle>
                              </div>
                              <CardDescription className="mt-1 text-sm">
                                {step.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </motion.div>
                );
              })}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:sticky lg:top-20 lg:self-start ">
            <AnimatePresence mode="wait">
              <motion.div
                  key={selectedStep?.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.2 }}
                  className="min-h-[420px]"
              >
                <Card className="min-h-[420px] border-primary/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{selectedStep?.title}</CardTitle>
                    </div>
                    <CardDescription>{selectedStep?.description}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    <div className="rounded-lg border border-border bg-surface-elevated p-4">
                      <p className="mb-2 text-xs font-medium text-muted-foreground">
                        WHAT WE’LL COVER
                      </p>
                      <pre className="overflow-x-auto text-sm leading-relaxed">
                      <code className="text-foreground min-h-[120px] block whitespace-pre-wrap">
                        {selectedStep?.code}
                      </code>
                    </pre>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
  );
};

export default DemoTimeline;
